import { useCallback } from 'react';
import { ExtendEstimateWithContext } from '../closeoutTypes';
import {
  useUpdateChangeOrderOverridesMutation,
  useUpdateEstimateOverridesMutation,
} from '../../../hooks/useCloseoutHooks';
import { formatCurrency } from '../closeoutHelpers';
import { calculateTotalMaterialCost, calculateTotalLaborHours } from './hooks';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../../stores/store';

export const virtualTableStyle = {
  name: '25%',
  attributeGroup: '15%',
  itemQty: '8%',
  itemUOM: '8%',
  itemUnitCost: '12%',
  totalMaterialCost: '12%',
  hourlyProductionRate: '12%',
  totalLaborHours: '8%',
  actions: '15%',
};

/**
 * Custom hook for handling unit cost updates
 */
export const useUnitCostUpdater = (
  item: ExtendEstimateWithContext,
  setLocalItem: React.Dispatch<React.SetStateAction<ExtendEstimateWithContext>>,
  setUnitCostInput: React.Dispatch<React.SetStateAction<string>>
) => {
  const { selectedChangeOrder } = useStore(useUnityBuildStore);
  const { mutate: updateEstimateExtension } = useUpdateEstimateOverridesMutation();
  const { mutate: updateChangeOrderExtension } = useUpdateChangeOrderOverridesMutation();
  const updateExtension = selectedChangeOrder
    ? updateChangeOrderExtension
    : updateEstimateExtension;

  // Check if this item represents aggregated items (has aggregatedExtensions array with multiple items)
  const isAggregated =
    Array.isArray(item.aggregatedExtensions) && item.aggregatedExtensions.length > 1;

  const commitUnitCostToServer = useCallback(
    (unitCostInput: string) => {
      const numericValue = Number(unitCostInput) || 0;

      // Update local state immediately to prevent UI flickers
      setLocalItem((prev: ExtendEstimateWithContext) => {
        // Calculate total cost with UOM adjustment
        const adjustedTotalCost = calculateTotalMaterialCost(
          numericValue,
          prev.totalitemqty,
          prev.itemuomvalue
        );

        return {
          ...prev,
          isoverrideitemunitcost: true,
          overrideitemunitcost: numericValue,
          totalcost: adjustedTotalCost,
        };
      });

      if (isAggregated) {
        // Update ALL aggregated items with the same name, regardless of breakout context
        // This ensures that when editing an aggregated row, all underlying items get updated
        const updatedItems = (item.aggregatedExtensions || []).map(
          (ext: ExtendEstimateWithContext) => {
            // Calculate adjusted cost using the utility function
            const adjustedTotalCost = calculateTotalMaterialCost(
              numericValue,
              ext.totalitemqty,
              ext.itemuomvalue
            );

            // Start with all fields from source object
            return {
              ...ext,
              // Set unit cost override values
              isoverrideitemunitcost: true,
              overrideitemunitcost: numericValue,
              totalcost: adjustedTotalCost,
              // Important: Preserve existing production rate override values
              // rather than using ones from the source object which might be stale
              isoverridehourlyproductionrate: ext.isoverridehourlyproductionrate,
              overridehourlyproductionrate: ext.overridehourlyproductionrate,
            };
          }
        );

        updateExtension({
          sessiontoken: '',
          extendestimatesList: updatedItems,
        });
      } else {
        // Single item update - calculate adjusted cost using the utility function
        const adjustedTotalCost = calculateTotalMaterialCost(
          numericValue,
          item.totalitemqty,
          item.itemuomvalue
        );

        // Include all fields from the original object
        const updatedItem = {
          ...item,
          // Set unit cost override values
          isoverrideitemunitcost: true,
          overrideitemunitcost: numericValue,
          totalcost: adjustedTotalCost,
          // Important: Preserve existing production rate override values
          // rather than using ones from the source object which might be stale
          isoverridehourlyproductionrate: item.isoverridehourlyproductionrate,
          overridehourlyproductionrate: item.overridehourlyproductionrate,
        };

        updateExtension({
          sessiontoken: '',
          extendestimatesList: [updatedItem],
        });
      }
    },
    [item, updateExtension, isAggregated, setLocalItem]
  );

  return { commitUnitCostToServer };
};

/**
 * Custom hook for handling production rate updates
 */
export const useProductionRateUpdater = (
  item: ExtendEstimateWithContext,
  setLocalItem: React.Dispatch<React.SetStateAction<ExtendEstimateWithContext>>,
  setProductionRateInput: React.Dispatch<React.SetStateAction<string>>
) => {
  const { selectedChangeOrder } = useStore(useUnityBuildStore);
  const { mutate: updateEstimateExtension } = useUpdateEstimateOverridesMutation();
  const { mutate: updateChangeOrderExtension } = useUpdateChangeOrderOverridesMutation();
  const updateExtension = selectedChangeOrder
    ? updateChangeOrderExtension
    : updateEstimateExtension;

  // Check if this item represents aggregated items (has aggregatedExtensions array with multiple items)
  const isAggregated =
    Array.isArray(item.aggregatedExtensions) && item.aggregatedExtensions.length > 1;

  const commitProductionRateToServer = useCallback(
    (productionRateInput: string) => {
      const numericValue = Number(productionRateInput) || 0;

      // Update local state immediately to prevent UI flickers
      setLocalItem((prev: ExtendEstimateWithContext) => {
        // Calculate labor hours using the utility function
        const newLaborHours = calculateTotalLaborHours(
          numericValue,
          prev.totalitemqty,
          prev.itemuomvalue
        );

        return {
          ...prev,
          isoverridehourlyproductionrate: true,
          overridehourlyproductionrate: numericValue,
          totaldirectlaborhours: newLaborHours,
        };
      });

      if (isAggregated) {
        // Update ALL aggregated items with the same name, regardless of breakout context
        // This ensures that when editing an aggregated row, all underlying items get updated
        const updatedItems = (item.aggregatedExtensions || []).map(
          (ext: ExtendEstimateWithContext) => {
            // Calculate labor hours using the utility function
            const newLaborHours = calculateTotalLaborHours(
              numericValue,
              ext.totalitemqty,
              ext.itemuomvalue
            );

            // Start with all fields from source object
            return {
              ...ext,
              // Important: Preserve existing unit cost override values
              // rather than using ones from the source object which might be stale
              isoverrideitemunitcost: ext.isoverrideitemunitcost,
              overrideitemunitcost: ext.overrideitemunitcost,
              // Set production rate override values
              isoverridehourlyproductionrate: true,
              overridehourlyproductionrate: numericValue,
              totaldirectlaborhours: newLaborHours,
            };
          }
        );

        updateExtension({
          sessiontoken: '',
          extendestimatesList: updatedItems,
        });
      } else {
        // Single item update - calculate labor hours using the utility function
        const newLaborHours = calculateTotalLaborHours(
          numericValue,
          item.totalitemqty,
          item.itemuomvalue
        );

        // Include all fields from the original object
        const updatedItem = {
          ...item,
          // Important: Preserve existing unit cost override values
          // rather than using ones from the source object which might be stale
          isoverrideitemunitcost: item.isoverrideitemunitcost,
          overrideitemunitcost: item.overrideitemunitcost,
          // Set production rate override values
          isoverridehourlyproductionrate: true,
          overridehourlyproductionrate: numericValue,
          totaldirectlaborhours: newLaborHours,
        };

        updateExtension({
          sessiontoken: '',
          extendestimatesList: [updatedItem],
        });
      }
    },
    [item, updateExtension, isAggregated, setLocalItem]
  );

  return { commitProductionRateToServer };
};

/**
 * Custom hook for handling reset of overrides
 */
export const useOverridesResetter = (
  item: ExtendEstimateWithContext,
  setLocalItem: React.Dispatch<React.SetStateAction<ExtendEstimateWithContext>>,
  setUnitCostInput: React.Dispatch<React.SetStateAction<string>>,
  setProductionRateInput: React.Dispatch<React.SetStateAction<string>>
) => {
  const { selectedChangeOrder } = useStore(useUnityBuildStore);

  const { mutate: updateEstimateExtension } = useUpdateEstimateOverridesMutation();
  const { mutate: updateChangeOrderExtension } = useUpdateChangeOrderOverridesMutation();
  const updateExtension = selectedChangeOrder
    ? updateChangeOrderExtension
    : updateEstimateExtension;

  // Check if this item represents aggregated items (has aggregatedExtensions array with multiple items)
  const isAggregated =
    Array.isArray(item.aggregatedExtensions) && item.aggregatedExtensions.length > 1;

  const resetOverrides = useCallback(() => {
    // Optimistic local update first
    setLocalItem((prev: ExtendEstimateWithContext) => ({
      ...prev,
      isoverrideitemunitcost: false,
      overrideitemunitcost: 0,
      isoverridehourlyproductionrate: false,
      overridehourlyproductionrate: 0,
    }));

    // Update input fields
    setUnitCostInput(formatCurrency(item.itemunitcost || 0));
    setProductionRateInput(item.hourlyproductionrate?.toFixed(3) || '0.000');

    // Then perform the server update
    if (isAggregated) {
      // Update ALL aggregated items with the same name, regardless of breakout context
      // This ensures that when resetting an aggregated row, all underlying items get reset
      const updatedItems = (item.aggregatedExtensions || []).map(
        (ext: ExtendEstimateWithContext) => {
          // Include all fields from the original object to satisfy typing
          return {
            ...ext, // Copy all fields from source object
            // Override specific fields we want to change
            isoverrideitemunitcost: false,
            overrideitemunitcost: 0,
            isoverridehourlyproductionrate: false,
            overridehourlyproductionrate: 0,
          };
        }
      );

      updateExtension({
        sessiontoken: '',
        extendestimatesList: updatedItems,
      });
    } else {
      // Include all fields from the original object
      const updatedItem = {
        ...item, // Copy all fields from source object
        // Override specific fields we want to change
        isoverrideitemunitcost: false,
        overrideitemunitcost: 0,
        isoverridehourlyproductionrate: false,
        overridehourlyproductionrate: 0,
      };

      updateExtension({
        sessiontoken: '',
        extendestimatesList: [updatedItem],
      });
    }
  }, [item, updateExtension, isAggregated, setLocalItem, setUnitCostInput, setProductionRateInput]);

  return { resetOverrides };
};

// Add this function to evaluate item last updated date
export const getItemLastUpdatedWarning = (lastUpdatedDate: string): 'warning' | 'error' | null => {
  if (!lastUpdatedDate) return null;

  try {
    const lastUpdated = new Date(lastUpdatedDate);
    const currentDate = new Date();

    // Calculate the difference in days
    const diffTime = currentDate.getTime() - lastUpdated.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays >= 60) {
      return 'error'; // More than 60 days - red warning
    } else if (diffDays >= 45) {
      return 'warning'; // Between 45-60 days - yellow warning
    }

    return null; // No warning needed
  } catch (error) {
    console.error('Error calculating item last updated warning:', error);
    return null;
  }
};
